import type { NavigationType } from 'react-router-dom'
import { createContext, createElement, useContext, type ReactNode } from 'react'

export interface RouteRef {
  key: string
  pathname: string
  search: string
  hash: string
}

interface Entry { from: RouteRef; to: RouteRef }

export interface RouteProvenanceSession {
  observe: (current: RouteRef, navigationType: NavigationType) => void
  predecessor: (key: string) => RouteRef | null
  hasPredecessor: (key: string) => boolean
  clear: () => void
}

function logicalRoute(ref: RouteRef): string {
  return ref.pathname
}

export function createRouteProvenanceSession(limit = 50): RouteProvenanceSession {
  const entries = new Map<string, Entry>()
  let previous: RouteRef | null = null
  let poppedBranchKey: string | null = null
  const deleteBranch = (rootKey: string) => {
    const doomed = new Set([rootKey])
    let changed = true
    while (changed) {
      changed = false
      for (const [key, entry] of entries) {
        if (doomed.has(entry.from.key) && !doomed.has(key)) { doomed.add(key); changed = true }
      }
    }
    doomed.forEach((key) => entries.delete(key))
  }
  return {
    observe(current, navigationType) {
      if (!previous) { previous = current; return }
      if (navigationType === 'PUSH') {
        if (poppedBranchKey) deleteBranch(poppedBranchKey)
        poppedBranchKey = null
        entries.set(current.key, { from: previous, to: current })
        while (entries.size > limit) entries.delete(entries.keys().next().value as string)
      } else if (navigationType === 'REPLACE') {
        const priorLineage = entries.get(previous.key)
        entries.delete(previous.key)
        entries.delete(current.key)
        if (logicalRoute(previous) === logicalRoute(current) && priorLineage) {
          entries.set(current.key, { from: priorLineage.from, to: current })
        }
        poppedBranchKey = null
      } else if (navigationType === 'POP') {
        // Back from a pushed entry identifies a forward branch. Retain it for
        // ordinary Forward, but discard it (and descendants) if a new PUSH
        // forks history from the popped-to entry.
        const priorLineage = entries.get(previous.key)
        poppedBranchKey = priorLineage?.from.key === current.key ? previous.key : null
      }
      previous = current
    },
    predecessor(key) { return entries.get(key)?.from ?? null },
    hasPredecessor(key) { return entries.has(key) },
    clear() { entries.clear(); previous = null; poppedBranchKey = null },
  }
}

const RouteProvenanceContext = createContext<RouteProvenanceSession | null>(null)

export function RouteProvenanceProvider({ session, children }: { session: RouteProvenanceSession; children: ReactNode }) {
  return createElement(RouteProvenanceContext.Provider, { value: session }, children)
}

export function useRouteProvenance(): RouteProvenanceSession {
  const ctx = useContext(RouteProvenanceContext)
  return ctx ?? createRouteProvenanceSession(0)
}
